import { useEffect, useId, useMemo, useState } from 'react'
import { getAuthInfo } from '@/utils/auth'
import {
  addDaysToDateString,
  calcChangePercent,
  findDailyCountByDate,
  getCouponUsedStatsDaily,
  getLastNDaysExclusiveRange,
  getMembersStatsDaily,
  getRewardStatsDaily,
  getTodayString,
  sumDailyCounts
} from '@/api/dashboard'
import { getProjectTimezone } from '@/utils/projectContext'
import styles from './StatDetailSheet.module.css'

export type StatDetailType = 'new-member' | 'points' | 'coupon'

type PeriodDays = 7 | 14 | 30

interface ChartPoint {
  date: string
  count: number
}

interface StatDetailSheetProps {
  open: boolean
  type: StatDetailType | null
  onClose: () => void
}

const PERIOD_OPTIONS: PeriodDays[] = [7, 14, 30]
/** 탭 전환 시 재호출 없이 자르기 위해 최대 기간만 조회 */
const FETCH_DAYS: PeriodDays = 30

const DETAIL_CONFIG: Record<
  StatDetailType,
  { title: string; unit: '명' | '건'; color: string }
> = {
  'new-member': { title: '오늘 신규 회원', unit: '명', color: '#6B73FF' },
  points: { title: '오늘 적립 현황', unit: '건', color: '#28c76f' },
  coupon: { title: '오늘 쿠폰 사용', unit: '건', color: '#ff9f43' }
}

function formatShortDate(dateStr: string): string {
  const [, m, d] = dateStr.split('-')
  return `${Number(m)}/${Number(d)}`
}

function formatDisplayDate(dateStr: string): string {
  const [y, m, d] = dateStr.split('-')
  return `${y}.${m}.${d}`
}

async function fetchDailyPoints(
  type: StatDetailType,
  projectCode: string,
  shopCode: string,
  days: PeriodDays,
  today: string,
  timezone: string | null
): Promise<ChartPoint[]> {
  const range = { ...getLastNDaysExclusiveRange(today, days), timezone }
  if (type === 'new-member') {
    const data = await getMembersStatsDaily(projectCode, shopCode, range)
    return data.points.map((p) => ({ date: p.date, count: p.newCount }))
  }
  if (type === 'points') {
    const data = await getRewardStatsDaily(projectCode, shopCode, range)
    return data.points.map((p) => ({ date: p.date, count: p.earnCount }))
  }
  const data = await getCouponUsedStatsDaily(projectCode, shopCode, range)
  return data.points.map((p) => ({ date: p.date, count: p.count }))
}

function sliceLastDays(points: ChartPoint[], days: PeriodDays): ChartPoint[] {
  if (points.length <= days) return points
  return points.slice(points.length - days)
}

function DetailBarChart({
  points,
  color,
  unit
}: {
  points: ChartPoint[]
  color: string
  unit: string
}) {
  const width = 360
  const height = 180
  const padLeft = 8
  const padRight = 8
  const padTop = 16
  const padBottom = 28
  const chartW = width - padLeft - padRight
  const chartH = height - padTop - padBottom
  const max = Math.max(...points.map((p) => p.count), 1)
  const gap = points.length > 20 ? 2 : 4
  const barW =
    points.length === 0 ? 0 : Math.max((chartW - gap * (points.length - 1)) / points.length, 2)

  if (points.length === 0) {
    return <p className={styles.emptyChart}>표시할 데이터가 없습니다.</p>
  }

  const labelStep = points.length <= 7 ? 1 : points.length <= 14 ? 2 : 5

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className={styles.chartSvg}
      role="img"
      aria-label={`일별 ${unit} 추이`}
    >
      {points.map((p, i) => {
        const h = (p.count / max) * chartH
        const x = padLeft + i * (barW + gap)
        const y = padTop + chartH - h
        const showLabel = i === 0 || i === points.length - 1 || i % labelStep === 0
        return (
          <g key={p.date}>
            <rect
              x={x}
              y={y}
              width={barW}
              height={Math.max(h, p.count > 0 ? 2 : 0)}
              rx={2}
              fill={color}
              opacity={i === points.length - 1 ? 1 : 0.75}
            >
              <title>{`${formatShortDate(p.date)}: ${p.count}${unit}`}</title>
            </rect>
            {showLabel && (
              <text
                x={x + barW / 2}
                y={height - 8}
                textAnchor="middle"
                className={styles.chartLabel}
              >
                {formatShortDate(p.date)}
              </text>
            )}
          </g>
        )
      })}
    </svg>
  )
}

export default function StatDetailSheet({ open, type, onClose }: StatDetailSheetProps) {
  const titleId = useId()
  const timezone = getProjectTimezone()
  const today = getTodayString(timezone)
  const yesterday = addDaysToDateString(today, -1)
  const [period, setPeriod] = useState<PeriodDays>(7)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [allPoints, setAllPoints] = useState<ChartPoint[]>([])

  useEffect(() => {
    if (!open) return
    setPeriod(7)
  }, [open, type])

  useEffect(() => {
    if (!open || type == null) return

    const authInfo = getAuthInfo()
    const projectCode = authInfo?.projectCode?.trim()
    const shopCode = authInfo?.shopCode?.trim()
    if (!projectCode || !shopCode) {
      setError('매장 정보가 없습니다. 다시 로그인해 주세요.')
      setAllPoints([])
      return
    }

    let cancelled = false
    setLoading(true)
    setError(null)

    fetchDailyPoints(type, projectCode, shopCode, FETCH_DAYS, today, timezone)
      .then((next) => {
        if (cancelled) return
        setAllPoints(next)
      })
      .catch((err) => {
        if (cancelled) return
        setAllPoints([])
        setError(err instanceof Error ? err.message : '상세 통계를 불러오지 못했습니다.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [open, type, today, timezone])

  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prev
      window.removeEventListener('keydown', onKey)
    }
  }, [open, onClose])

  const points = useMemo(() => sliceLastDays(allPoints, period), [allPoints, period])
  const tableRows = useMemo(
    () => [...points].reverse(),
    [points]
  )

  if (!open || type == null) return null

  const config = DETAIL_CONFIG[type]
  const todayCount = findDailyCountByDate(allPoints, today, (p) => p.count)
  const yesterdayCount = findDailyCountByDate(allPoints, yesterday, (p) => p.count)
  const changePercent = calcChangePercent(todayCount, yesterdayCount)
  const periodSum = sumDailyCounts(points, (p) => p.count)
  const isPositive = changePercent === null ? null : changePercent >= 0
  const countHeader = config.unit === '명' ? '회원 수' : '건수'

  return (
    <div className={styles.root} role="presentation">
      <button type="button" className={styles.backdrop} aria-label="닫기" onClick={onClose} />
      <div
        className={styles.sheet}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className={styles.handle} aria-hidden />
        <header className={styles.header}>
          <div>
            <h2 id={titleId} className={styles.title}>
              {config.title}
            </h2>
            <p className={styles.date}>{formatDisplayDate(today)}</p>
          </div>
          <button type="button" className={styles.closeButton} onClick={onClose} aria-label="닫기">
            ×
          </button>
        </header>

        <div className={styles.summary}>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>오늘</span>
            <span className={styles.summaryValue}>
              {loading ? '…' : `${todayCount}${config.unit}`}
            </span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>어제</span>
            <span className={styles.summaryValue}>
              {loading ? '…' : `${yesterdayCount}${config.unit}`}
            </span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>전일 대비</span>
            <span
              className={`${styles.summaryValue} ${
                isPositive === true
                  ? styles.positive
                  : isPositive === false
                    ? styles.negative
                    : ''
              }`}
            >
              {loading || changePercent === null
                ? '-'
                : `${isPositive ? '▲' : '▼'} ${Math.abs(changePercent)}%`}
            </span>
          </div>
        </div>

        <div className={styles.contentSection}>
          {error ? (
            <p className={styles.chartError}>{error}</p>
          ) : (
            <div className={styles.split}>
              <section className={styles.chartPane} aria-label="추이 그래프">
                <div className={styles.chartPaneHeader}>
                  <h3 className={styles.paneTitle}>추이</h3>
                  <div className={styles.periodTabs} role="tablist" aria-label="조회 기간">
                    {PERIOD_OPTIONS.map((days) => (
                      <button
                        key={days}
                        type="button"
                        role="tab"
                        aria-selected={period === days}
                        className={`${styles.periodTab} ${period === days ? styles.periodTabActive : ''}`}
                        onClick={() => setPeriod(days)}
                      >
                        {days}일
                      </button>
                    ))}
                  </div>
                </div>
                {loading ? (
                  <div className={styles.chartLoading}>불러오는 중…</div>
                ) : (
                  <>
                    <DetailBarChart points={points} color={config.color} unit={config.unit} />
                    <p className={styles.periodSum}>
                      최근 {period}일 합계 {periodSum}
                      {config.unit}
                    </p>
                  </>
                )}
              </section>

              <section className={styles.tableSection} aria-label="일자별 내역">
                <h3 className={styles.paneTitle}>일자별 내역</h3>
                {loading ? (
                  <div className={styles.chartLoading}>불러오는 중…</div>
                ) : tableRows.length === 0 ? (
                  <p className={styles.emptyTable}>표시할 데이터가 없습니다.</p>
                ) : (
                  <div className={styles.tableWrap}>
                    <table className={styles.table}>
                      <thead>
                        <tr>
                          <th scope="col">날짜</th>
                          <th scope="col" className={styles.numCol}>
                            {countHeader}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {tableRows.map((row) => (
                          <tr
                            key={row.date}
                            className={row.date === today ? styles.todayRow : undefined}
                          >
                            <td>
                              {formatDisplayDate(row.date)}
                              {row.date === today ? (
                                <span className={styles.todayBadge}>오늘</span>
                              ) : null}
                            </td>
                            <td className={styles.numCol}>
                              {row.count}
                              {config.unit}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
